import { Injectable, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter } from 'rxjs/operators';

/** A router URL without its query string and fragment. */
function pathOf(url: string): string {
  return url.split(/[?#]/u)[0];
}

/**
 * Whether the app is in "edit table layout" mode.
 *
 * A table header is a dense place — a ⋮ button on every column competes with the
 * labels, the sort arrows and the resize grips for the same few pixels. So the
 * per-column options menu and drag-to-reorder are off by default and revealed
 * together, on demand, from the toolbar's table settings gear. Resizing and
 * sorting are always available; only the chrome you need while *rearranging*
 * columns is gated.
 *
 * The mode belongs to the page it was turned on from: navigating to another page
 * ends it, so that page's gear offers "Edit Table Layout" again rather than
 * "Done Editing Layout" for a change made elsewhere. It lives only in memory: it
 * is a short-lived task, so a reload always comes back to the clean read-only
 * header. Nothing here is persisted — that's
 * {@link TrTableStateService}'s job, and only for the layout itself.
 */
@Injectable({ providedIn: 'root' })
export class TrTableLayoutModeService {
  private readonly editingSubject = new BehaviorSubject<boolean>(false);
  private readonly dirtySubject = new BehaviorSubject<boolean>(false);
  private readonly router = inject(Router);

  /** Emits the current mode, starting with its present value. */
  readonly editing$: Observable<boolean> = this.editingSubject.asObservable();

  /**
   * Emits whether the layout has changes that a location default wouldn't have —
   * what the gear's unsaved dot is bound to.
   */
  readonly dirty$: Observable<boolean> = this.dirtySubject.asObservable();

  constructor() {
    // Leaving the page ends editing and abandons the reminder along with the
    // visible table: the device layout itself is still saved (localStorage), what's
    // dropped is the edit mode and the "you haven't published this to the location"
    // nudge. Compared by path, so a query-param-only change (the waitlist's party
    // highlight, say) keeps you editing. Root service, so this subscription lives
    // as long as the app does.
    let path = pathOf(this.router.url);
    this.router.events
      .pipe(filter((routerEvent): routerEvent is NavigationEnd => routerEvent instanceof NavigationEnd))
      .subscribe(({ urlAfterRedirects }) => {
        const nextPath = pathOf(urlAfterRedirects);
        if (nextPath !== path) {
          path = nextPath;
          this.set(false);
          this.clearDirty();
        }
      });
  }

  get editing(): boolean {
    return this.editingSubject.value;
  }

  get dirty(): boolean {
    return this.dirtySubject.value;
  }

  /**
   * A column moved, pinned, unpinned, shown or hidden — a change a location
   * default would carry. Widths are deliberately *not* reported here: they stay
   * device-local and are never part of a location default.
   */
  markDirty(): void {
    this.setDirty(true);
  }

  /** Saved or reset to the location default — nothing left to publish. */
  clearDirty(): void {
    this.setDirty(false);
  }

  private setDirty(dirty: boolean): void {
    if (dirty !== this.dirtySubject.value) {
      this.dirtySubject.next(dirty);
    }
  }

  set(editing: boolean): void {
    if (editing !== this.editingSubject.value) {
      this.editingSubject.next(editing);
    }
  }

  toggle(): void {
    this.set(!this.editing);
  }
}
